import { Component, Input, OnInit, OnDestroy, AfterViewInit, ElementRef, Renderer2, ViewContainerRef, ComponentRef, ViewEncapsulation } from '@angular/core';
import { ContentLoaderService, ContentData } from '../../services/content-loader.service';
import { Subscription } from 'rxjs';
import { GearTierIconComponent } from '../icons/gear-tier-icon/gear-tier-icon.component';
import { BaseTypeTier } from '../../models/enums';
import { poe1, poe2 } from '../../../game';

@Component({
  selector: 'app-content',
  standalone: true,
  imports: [],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './dynamic-content.component.html',
  styleUrls: ['./dynamic-content.component.scss']
})
export class DynamicContentComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() contentPath!: string;
  @Input({ required: true }) cid!: string;
  @Input() contentType: 'tutorial' | 'tip' = 'tutorial';
  @Input() language = 'en';
  @Input() conditions: Record<string, any> = {};

  content: string | null = null;
  loading = false;
  error: string | null = null;
  private subscription?: Subscription;
  private componentRefs: ComponentRef<any>[] = [];

  constructor(
    private contentLoader: ContentLoaderService,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef
  ) {}

  ngOnInit(): void {
    this.loadContent();
  }

  ngAfterViewInit(): void {
    if (this.content && !this.loading && !this.error) {
      setTimeout(() => {
        this.setupConditionalContent();
      }, 0);
    }
  }

  public updateConditions(newConditions: Record<string, any>): void {
    this.conditions = newConditions;
    if (this.content && !this.loading && !this.error) {
      setTimeout(() => {
        this.setupConditionalContent();
      }, 0);
    }
  }

  public refreshConditionalContent(): void {
    if (this.content && !this.loading && !this.error) {
      setTimeout(() => {
        this.setupConditionalContent();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.componentRefs.forEach(ref => ref.destroy());
  }

  loadContent(): void {
    if (!this.cid) {
      this.error = 'Content ID is required';
      return;
    }

    this.loading = true;
    this.error = null;

    this.subscription = this.contentLoader.loadContent(this.cid, this.contentType, this.language)
      .subscribe({
        next: (contentData: ContentData) => {
          this.loading = false;
          if (contentData.loaded) {
            this.content = contentData.content as string;
            setTimeout(() => {
              this.setupInteractiveElements();
              this.replaceCustomComponents();
              this.setupConditionalContent();
            }, 0);
          } else {
            this.error = contentData.error || 'Failed to load content';
          }
        },
        error: (err) => {
          this.loading = false;
          this.error = err.message || 'Failed to load content';
        }
      });
  }

  handleClick(event: Event): void {
    const target = event.target as HTMLElement;

    if (target.hasAttribute('data-toggle-target')) {
      event.preventDefault();
      event.stopPropagation();
      const toggleTarget = target.getAttribute('data-toggle-target');
      if (toggleTarget) {
        this.toggleSection(toggleTarget);
      }
    }
  }

  private setupInteractiveElements(): void {
    const container = this.elementRef.nativeElement.querySelector('.dynamic-content');
    if (!container) return;

    const toggleLinks = container.querySelectorAll('[data-toggle-target]');
    toggleLinks.forEach((link: Element) => {
      this.renderer.listen(link, 'click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const targetId = link.getAttribute('data-toggle-target');
        if (targetId) {
          this.toggleSection(targetId);
        }
      });
    });
  }

  public setupConditionalContent(): void {
    const container = this.elementRef.nativeElement.querySelector('.dynamic-content');
    if (!container) return;

    const conditionalElements = container.querySelectorAll('[data-display-when]');

    conditionalElements.forEach((element: Element) => {
      const conditionName = element.getAttribute('data-display-when');
      if (conditionName) {
        this.evaluateCondition(element as HTMLElement, conditionName);
      }
    });
  }

  private get mergedConditions(): Record<string, any> {
    // Automatically append poe1 and poe2 from game.ts constants
    // Input conditions take precedence if there's a conflict
    return {
      poe1,
      poe2,
      ...this.conditions
    };
  }

  private evaluateCondition(element: HTMLElement, conditionName: string): void {
    const conditionValue = this.mergedConditions[conditionName];

    element.classList.add('conditional-content');

    if (conditionValue) {
      element.classList.add('visible');
    } else {
      element.classList.remove('visible');
    }
  }

  private replaceCustomComponents(): void {
    const container = this.elementRef.nativeElement.querySelector('.dynamic-content');
    if (!container) return;

    const gearTierIcons = container.querySelectorAll('gear-tier-icon');
    gearTierIcons.forEach((element: Element) => {
      const tier = element.getAttribute('tier');
      if (tier) {
        this.createGearTierIcon(element as HTMLElement, tier as BaseTypeTier);
      }
    });
  }

  private createGearTierIcon(element: HTMLElement, tier: BaseTypeTier): void {
    const componentRef = this.viewContainerRef.createComponent(GearTierIconComponent);
    componentRef.setInput('tier', tier);

    element.parentNode?.replaceChild(componentRef.location.nativeElement, element);

    this.componentRefs.push(componentRef);
  }

  private toggleSection(toggleId: string): void {
    const container = this.elementRef.nativeElement.querySelector('.dynamic-content');
    if (!container) return;

    const targetElement = container.querySelector(`[data-toggle-id="${toggleId}"]`);
    if (targetElement) {
      targetElement.classList.toggle('hidden');

      const link = container.querySelector(`[data-toggle-target="${toggleId}"]`);
      if (link) {
        const isHidden = targetElement.classList.contains('hidden');
        link.textContent = isHidden ? '(show more)' : '(show less)';
      }
    }
  }
}
